import React, { ReactNode } from "react";
import {
  Box,
  CollectionPreferencesProps,
  Link,
} from "@amzn/awsui-components-console";
import { createTableSortLabelFn } from "src/i18n-strings/table";

/**
 * Parses the additionalInfo string and renders known fields as links.
 *
 * Known patterns:
 *   Plan Id: <id>           → links to /getplan/<id>
 *   ARD Id: <id>            → links to /jemgen?selectedArdId=<id>
 *   External Id: <id>       → links to /transactionsearch/external_id/<id>
 *   Bulk Request Id: <id>   → links to /bulkredrivestatus/<id>
 *   Fixed Fee Id: <id>      → links to /getfixedfeeconfig/<id>
 *
 * Everything else is rendered as plain text.
 */
function renderAdditionalInfo(additionalInfo: string): React.ReactNode {
  if (!additionalInfo) {
    return additionalInfo;
  }

  const parts = additionalInfo.split(", ");
  const rendered: React.ReactNode[] = [];

  for (const part of parts) {
    const [key, ...rest] = part.split(": ");
    const value = rest.join(": ").trim();

    if (!value) {
      rendered.push(<span key={rendered.length}>{part}</span>);
      continue;
    }

    const trimmedKey = key.trim();

    if (trimmedKey === "Plan Id") {
      rendered.push(
        <span key={rendered.length}>
          Plan Id: <Link href={`/getplan/${value}`}>{value}</Link>
        </span>,
      );
    } else if (trimmedKey === "ARD Id") {
      rendered.push(
        <span key={rendered.length}>
          ARD Id: <Link href={`/jemgen?selectedArdId=${value}`}>{value}</Link>
        </span>,
      );
    } else if (trimmedKey === "External Id") {
      rendered.push(
        <span key={rendered.length}>
          External Id:{" "}
          <Link href={`/transactionsearch/external_id/${value}`}>{value}</Link>
        </span>,
      );
    } else if (trimmedKey === "Bulk Request Id") {
      rendered.push(
        <span key={rendered.length}>
          Bulk Request Id:{" "}
          <Link href={`/bulkredrivestatus/${value}`}>{value}</Link>
        </span>,
      );
    } else if (trimmedKey === "Fixed Fee Id") {
      rendered.push(
        <span key={rendered.length}>
          Fixed Fee Id:{" "}
          <Link href={`/getfixedfeeconfig/${value}`}>{value}</Link>
        </span>,
      );
    } else {
      rendered.push(<span key={rendered.length}>{part}</span>);
    }
  }

  return (
    <span>
      {rendered.map((node, i) => (
        <React.Fragment key={i}>
          {i > 0 && ", "}
          {node}
        </React.Fragment>
      ))}
    </span>
  );
}

const rawColumns = [
  {
    id: "operationType",
    header: "Operation Type",
    cell: (item: { operationType: any }) => item.operationType,
    minWidth: 80,
    maxWidth: 80,
    sortingField: "operationType",
  },
  {
    id: "userId",
    header: "User Id",
    cell: (item: { userId: any }) => item.userId,
    minWidth: 60,
    maxWidth: 60,
  },
  {
    id: "requestTime",
    header: "Request Time",
    cell: (item: { requestTime: any }) => item.requestTime,
    minWidth: 100,
    maxWidth: 100,
  },
  {
    id: "reasoning",
    header: "Operation Reasoning",
    cell: (item: { reasoning: any }) => item.reasoning,
    minWidth: 175,
    maxWidth: 175,
  },
  {
    id: "additionalInfo",
    header: "Additional Info",
    cell: (item: { additionalInfo: any }) =>
      renderAdditionalInfo(item.additionalInfo),
    minWidth: 250,
    maxWidth: 250,
  },
];

export const AUDIT_COLUMN_DEFINITIONS = rawColumns.map((column) => ({
  ...column,
  ariaLabel: createTableSortLabelFn(column),
}));

export function getMatchesCountText(count: number) {
  return count === 1 ? `1 match` : `${count} matches`;
}

interface EmptyStateProps {
  title: string;
  subtitle: string;
  action: ReactNode;
}
export function EmptyState({ title, subtitle, action }: EmptyStateProps) {
  return (
    <Box textAlign="center" color="inherit">
      <Box variant="strong" textAlign="center" color="inherit">
        {title}
      </Box>
      <Box variant="p" padding={{ bottom: "s" }} color="inherit">
        {subtitle}
      </Box>
      {action}
    </Box>
  );
}

export const auditPaginationLabels = {
  nextPageLabel: "Next page",
  pageLabel: (pageNumber: number) => `Go to page ${pageNumber}`,
  previousPageLabel: "Previous page",
};

export const AUDIT_DEFAULT_PREFERENCES: CollectionPreferencesProps.Preferences =
  {
    pageSize: 10,
    visibleContent: [
      "operationType",
      "userId",
      "requestTime",
      "reasoning",
      "additionalInfo",
    ],
    wrapLines: false,
    custom: "table",
  };
